import { useState, useEffect, useRef, useMemo } from 'react';
import api from '@/lib/api';
import ZLoadingBar from '@/components/shared/ZLoadingBar';
import ZEmptyState from '@/components/shared/ZEmptyState';
import ZTile from '@/components/shared/ZTile';

// Golf tab for Verve. Reads /verve/golf/dashboard, writes only through the
// confirm card (POST /verve/golf/rounds). Styling follows Verve.js inline
// conventions: MONO labels, SERIF hero numbers, red accent.

const RED = '#FF0042';
const GREEN = '#10B981';
const AMBER = '#E8C547';
const DIM = '#6b7084';
const MID = '#b0b4c0';
const TEXT = '#f0f0f5';
const MONO = "'JetBrains Mono', monospace";
const SERIF = "'Cormorant Garamond', Georgia, serif";
const SANS = "'Inter', sans-serif";

const fmtDate = (iso) => new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' });
const short = (c) => (c || '').replace(' GOLF CLUB', '').replace(' COUNTRY CLUB', '').replace(' GOLF AND TROUT ESTATE', '');

function Label({ children, right }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '22px 0 10px' }}>
      <span style={{ fontFamily: MONO, fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: MID }}>{children}</span>
      {right}
    </div>
  );
}

function Row({ k, v, sub, color }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '7px 0', borderBottom: '0.5px solid rgba(255,255,255,0.04)' }}>
      <span style={{ fontFamily: MONO, fontSize: 11, color: MID, letterSpacing: '0.08em' }}>{k}</span>
      <span style={{ fontFamily: MONO, fontSize: 13, color: color || TEXT }}>{v}{sub && <span style={{ fontSize: 10, color: DIM, marginLeft: 6 }}>{sub}</span>}</span>
    </div>
  );
}

const btn = (primary) => ({
  background: primary ? RED : 'none', border: `0.5px solid ${primary ? RED : RED + '44'}`, color: primary ? '#fff' : RED,
  borderRadius: 4, padding: '4px 10px', fontFamily: MONO, fontSize: 10, cursor: 'pointer', letterSpacing: '0.06em',
});
const input = { background: 'rgba(255,255,255,0.05)', border: '0.5px solid rgba(255,255,255,0.10)', borderRadius: 6, padding: '7px 10px', color: TEXT, fontFamily: MONO, fontSize: 12, outline: 'none', width: '100%' };

// ── Index chart: published index vs uncapped, whole history ──────────────────
function IndexChart({ series, onPick }) {
  const W = 340, H = 150, P = { l: 26, r: 6, t: 8, b: 18 };
  const pts = series.filter(s => s.index_before != null);
  if (pts.length < 2) return null;
  const t0 = new Date(pts[0].date).getTime(), t1 = new Date(pts[pts.length - 1].date).getTime();
  const vals = pts.flatMap(p => [p.index_before, p.uncapped].filter(v => v != null));
  const lo = Math.floor(Math.min(...vals)), hi = Math.ceil(Math.max(...vals));
  const x = (d) => P.l + ((new Date(d).getTime() - t0) / (t1 - t0 || 1)) * (W - P.l - P.r);
  const y = (v) => P.t + (1 - (v - lo) / (hi - lo || 1)) * (H - P.t - P.b);
  const path = (key) => pts.filter(p => p[key] != null).map((p, i) => `${i ? 'L' : 'M'}${x(p.date).toFixed(1)},${y(p[key]).toFixed(1)}`).join(' ');
  const years = [];
  for (let yr = new Date(pts[0].date).getFullYear() + 1; yr <= new Date(pts[pts.length - 1].date).getFullYear(); yr++) years.push(yr);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }}
      onClick={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        const px = ((e.clientX - r.left) / r.width) * W;
        let best = pts[0], bd = 1e9;
        pts.forEach(p => { const d = Math.abs(x(p.date) - px); if (d < bd) { bd = d; best = p; } });
        onPick && onPick(best);
      }}>
      {[lo, Math.round((lo + hi) / 2), hi].map(v => (
        <g key={v}><line x1={P.l} x2={W - P.r} y1={y(v)} y2={y(v)} stroke="rgba(255,255,255,0.06)" strokeWidth="0.5" />
          <text x={P.l - 4} y={y(v) + 3} fontSize="8" fill={DIM} fontFamily={MONO} textAnchor="end">{v}</text></g>
      ))}
      {years.map(yr => <text key={yr} x={x(`${yr}-01-01`)} y={H - 5} fontSize="8" fill={DIM} fontFamily={MONO} textAnchor="middle">{String(yr).slice(2)}</text>)}
      <path d={path('uncapped')} fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="1" />
      <path d={path('index_before')} fill="none" stroke={RED} strokeWidth="1.4" />
    </svg>
  );
}

// ── Last 20: differentials as bars, counting rounds lit ──────────────────────
// Bars run oldest (left, leaves next) to newest. Under each counting bar: how many
// more rounds until it leaves the window. Amber dashed: 8th best. Green dashed:
// the differential the next round must beat to lower the index.
function WindowBars({ window, eighth, lowerAt, onPick }) {
  const W = 340, H = 124, P = { l: 22, r: 4, t: 6, b: 22 };
  const vals = window.map(w => w.score_diff ?? 0);
  const lo = Math.min(0, ...vals), hi = Math.max(...vals) + 1;
  const bw = (W - P.l - P.r) / window.length;
  const y = (v) => P.t + (1 - (v - lo) / (hi - lo || 1)) * (H - P.t - P.b);
  const full = window.length >= 20;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
      {eighth != null && <line x1={P.l} x2={W - P.r} y1={y(eighth)} y2={y(eighth)} stroke={AMBER} strokeWidth="0.6" strokeDasharray="3 3" />}
      {lowerAt != null && lowerAt !== eighth && <line x1={P.l} x2={W - P.r} y1={y(lowerAt)} y2={y(lowerAt)} stroke={GREEN} strokeWidth="0.6" strokeDasharray="1 2" />}
      {[lo, hi].map(v => <text key={v} x={P.l - 4} y={y(v) + 3} fontSize="8" fill={DIM} fontFamily={MONO} textAnchor="end">{Math.round(v)}</text>)}
      {window.map((w, i) => (
        <g key={w.id || i} onClick={() => onPick && onPick(w)} style={{ cursor: 'pointer' }}>
          <rect x={P.l + i * bw + 1} width={Math.max(2, bw - 2)} y={y(w.score_diff ?? 0)} height={Math.max(1, y(lo) - y(w.score_diff ?? 0))}
            fill={w.counts ? GREEN : 'rgba(255,255,255,0.14)'} rx="1"
            stroke={full && i === 0 ? RED : 'none'} strokeWidth="0.8" />
          {full && w.counts && <text x={P.l + i * bw + bw / 2} y={H - 12} fontSize="7" fill={GREEN} fontFamily={MONO} textAnchor="middle">{i + 1}</text>}
        </g>
      ))}
      {full && <text x={P.l + bw / 2} y={H - 3} fontSize="7" fill={RED} fontFamily={MONO} textAnchor="start">out next</text>}
      <text x={W - P.r} y={H - 3} fontSize="7" fill={DIM} fontFamily={MONO} textAnchor="end">newest</text>
    </svg>
  );
}

// ── Next round: what to shoot, at a chosen course ───────────────────────────
// Backend gives thresholds as differentials (course-free); gross at a course is
// the highest integer whose differential, rounded as HNA does, is at or under it.
const diffOf = (g, cr, slope) => Math.round(((g - cr) * 113 / slope) * 10 + 1e-6) / 10;
function maxGross(x, cr, slope) {
  let g = Math.floor(cr + (x * slope) / 113) + 2;
  while (diffOf(g, cr, slope) > x) g--;
  return g;
}
const bandIndex = (ladder, x) => (ladder.find(b => (b.diff_min == null || x >= b.diff_min) && (b.diff_max == null || x <= b.diff_max)) || ladder[ladder.length - 1]).index;

const dayMonth = (iso) => new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
const f1 = (v) => (v == null ? '-' : Number(v).toFixed(1));

// Average of (differential - index going in) across the whole record: how far
// over his index Werner normally plays. The fair yardstick for today's gap.
function typicalGap(series) {
  const g = (series || []).filter(s => s.score_diff != null && s.index_before != null).map(s => s.score_diff - s.index_before);
  return g.length ? g.reduce((a, b) => a + b, 0) / g.length : null;
}

export function buildStory(cur, series) {
  const n = cur.next, idx = cur.index, out = [];
  if (idx == null) return out;
  out.push({ t: `You're playing like a ${f1(cur.last20_avg)} lately. Your index is ${f1(idx)} because it only counts your best 8 of the last 20.` });

  const normal = typicalGap(series);
  if (normal != null && cur.gap_to_index != null) {
    const d = cur.gap_to_index - normal, g = f1(cur.gap_to_index), u = f1(normal);
    out.push(
      d > 1 ? { t: `That gap of ${g} is much wider than your usual ${u}: the index is well ahead of your form.`, tone: 'warn' }
      : d > 0.3 ? { t: `That gap of ${g} is a bit wider than your usual ${u}.`, tone: 'warn' }
      : d < -0.3 ? { t: `That gap of ${g} is narrower than your usual ${u}: your form is backing the index up.`, tone: 'good' }
      : { t: `That gap of ${g} is about your usual ${u}.` });
  }
  if (!n) return out;

  const worst = n.drift_at_worst || [];
  let safe = 0;
  while (safe < worst.length && worst[safe] <= idx) safe++;
  out.push(safe > 0
    ? { t: `Nothing you shoot in the next ${safe === 1 ? 'round' : `${safe} rounds`} can raise it.`, tone: 'good' }
    : { t: 'A bad next round will raise it.', tone: 'warn' });

  const path = n.drift_at_average?.path || [];
  if (path.length) {
    const end = path[path.length - 1];
    let s = end > idx ? `Play your average (${f1(n.drift_at_average.diff)}) and it drifts to ${f1(end)} over ${path.length} rounds.`
      : end < idx ? `Play your average and it still edges down to ${f1(end)}.`
      : `Play your average and it holds at ${f1(idx)}.`;
    // The round that leaves on round k+1 is window[k] (the window is full, oldest first).
    const full = (cur.window || []).length >= 20;
    const steps = [];
    path.forEach((v, k) => {
      const w = cur.window?.[k];
      if (full && w?.counts && v - (k ? path[k - 1] : idx) >= 0.3) steps.push({ round: k + 1, w });
    });
    if (end > idx && steps.length) {
      const many = steps.length > 1;
      s += ` The big step${many ? 's come' : ' comes'} at round ${steps.map(x => x.round).join(' and ')}, when your ${steps.map(x => `${dayMonth(x.w.date)} (${x.w.score_diff})`).join(' and ')} drop${many ? '' : 's'} out.`;
    }
    out.push({ t: s, tone: end > idx ? 'warn' : end < idx ? 'good' : undefined });
  }
  return out;
}

function Story({ lines }) {
  if (!lines.length) return null;
  return (
    <div style={{ marginTop: 18, textAlign: 'left' }}>
      {lines.map((l, i) => (
        <p key={i} style={{ fontFamily: SANS, fontSize: 14, lineHeight: 1.5, color: TEXT, margin: '0 0 8px', paddingLeft: 10,
          borderLeft: `2px solid ${l.tone === 'warn' ? AMBER : l.tone === 'good' ? GREEN : 'rgba(255,255,255,0.12)'}` }}>{l.t}</p>
      ))}
    </div>
  );
}

function PickBox({ pick, onCourse }) {
  if (!pick) return null;
  return (
    <div style={{ marginTop: 8, padding: '8px 12px', background: 'rgba(255,255,255,0.03)', borderRadius: 6 }}>
      <span onClick={() => pick.course && onCourse(pick.course)} style={{ fontFamily: SANS, fontSize: 12, color: TEXT, cursor: 'pointer' }}>{fmtDate(pick.date)} · {short(pick.course)}</span>
      <span style={{ fontFamily: MONO, fontSize: 11, color: DIM, marginLeft: 8 }}>
        {pick.adj_gross != null ? `${pick.adj_gross}, ` : ''}diff {pick.score_diff}{pick.index_before != null ? ` · index ${pick.index_before}` : ''}{pick.uncapped != null ? ` · uncapped ${pick.uncapped}` : ''}
      </span>
    </div>
  );
}

const ratedCourses = (courses) => (courses || []).filter(c => c.latest_rating?.course_rating && c.latest_rating?.slope);
const defaultRated = (rated, defaultCourse) => rated.find(c => c.course === defaultCourse) || rated[0];

// One plain sentence for a course: what next round's score does to the index.
// Shared by the Next round tile's summary and its expanded view.
export function nextRoundLine(n, rating) {
  const { course_rating: cr, slope } = rating;
  const idx = n.index;
  const lowerG = n.lower_max_diff != null ? maxGross(n.lower_max_diff, cr, slope) : null;
  const holdG = !n.any_score_holds && n.hold_max_diff != null ? maxGross(n.hold_max_diff, cr, slope) : null;
  if (n.any_score_holds) {
    return lowerG != null
      ? `${lowerG} or better lowers it. ${lowerG + 1} or worse leaves it at ${f1(idx)}.`
      : `Nothing you shoot here next round moves it: it stays ${f1(idx)}.`;
  }
  const riseFrom = holdG != null ? holdG + 1 : (lowerG != null ? lowerG + 1 : null);
  const parts = [];
  if (lowerG != null) parts.push(`${lowerG} or better lowers it.`);
  if (holdG != null && (lowerG == null || holdG > lowerG)) parts.push(`${lowerG != null ? lowerG + 1 : 'Up'} to ${holdG} leaves it at ${f1(idx)}.`);
  if (riseFrom != null) parts.push(`${riseFrom} or worse raises it, as far as ${f1(n.worst_index)}.`);
  return parts.join(' ');
}

function NextRound({ n, courses, defaultCourse }) {
  const rated = useMemo(() => ratedCourses(courses), [courses]);
  const [name, setName] = useState(() => defaultRated(rated, defaultCourse)?.course);
  const c = rated.find(x => x.course === name);
  if (!c) return null;
  const { course_rating: cr, slope, tee, par } = c.latest_rating;
  const idx = n.index;
  const lowerG = n.lower_max_diff != null ? maxGross(n.lower_max_diff, cr, slope) : null;
  const line = nextRoundLine(n, c.latest_rating);

  const pivot = lowerG ?? maxGross(idx, cr, slope);
  const cells = [];
  for (let g = pivot - 6; g <= pivot + 6; g++) {
    const i = bandIndex(n.ladder, diffOf(g, cr, slope));
    const last = cells[cells.length - 1];
    if (last && last.idx === i) last.to = g; else cells.push({ from: g, to: g, idx: i });
  }
  const cellLabel = (x, i) => (i === 0 ? `≤${x.to}` : i === cells.length - 1 ? `${x.from}+` : x.from === x.to ? x.from : `${x.from}-${x.to}`);
  const col = (v) => (v < idx ? GREEN : v > idx ? RED : MID);

  return (
    <>
      <select value={name} onChange={e => setName(e.target.value)} style={{ ...input, appearance: 'none', marginBottom: 6 }}>
        {rated.map(x => <option key={x.course} value={x.course}>{short(x.course)}</option>)}
      </select>
      <p style={{ fontFamily: MONO, fontSize: 9, color: DIM, marginBottom: 10 }}>
        {tee || ''} · CR {cr} · slope {slope}{par ? ` · par ${par}` : ''} · course hcp {Math.round(idx * slope / 113 + (cr - (par || 72)))}
      </p>
      <p style={{ fontFamily: SANS, fontSize: 14, lineHeight: 1.5, color: TEXT, marginBottom: 10 }}>{line}</p>
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        {cells.map((x, i) => (
          <div key={x.from} style={{ flex: '1 0 44px', background: 'rgba(255,255,255,0.03)', borderRadius: 6, padding: '6px 2px', textAlign: 'center', borderBottom: `1.5px solid ${col(x.idx)}` }}>
            <div style={{ fontFamily: MONO, fontSize: 11, color: TEXT }}>{cellLabel(x, i)}</div>
            <div style={{ fontFamily: MONO, fontSize: 9, color: col(x.idx), marginTop: 2 }}>{x.idx?.toFixed(1)}</div>
          </div>
        ))}
      </div>
      <p style={{ fontFamily: MONO, fontSize: 9, color: DIM, marginTop: 6 }}>top: your score here · bottom: your index after it · assumes PCC 0</p>
    </>
  );
}

function Drift({ n }) {
  const path = n.drift_at_average.path;
  return (
    <>
      <div style={{ fontFamily: MONO, fontSize: 9, color: DIM, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 6 }}>
        If you play your average ({n.drift_at_average.diff}) every round
      </div>
      <div style={{ display: 'flex', gap: 4 }}>
        {path.map((v, i) => (
          <div key={i} style={{ flex: 1, textAlign: 'center', background: 'rgba(255,255,255,0.03)', borderRadius: 4, padding: '5px 0' }}>
            <div style={{ fontFamily: MONO, fontSize: 8, color: DIM }}>{i + 1}</div>
            <div style={{ fontFamily: MONO, fontSize: 11, color: v > n.index ? RED : v < n.index ? GREEN : TEXT }}>{v?.toFixed(1)}</div>
          </div>
        ))}
      </div>
      <p style={{ fontFamily: MONO, fontSize: 9, color: DIM, marginTop: 6 }}>top: rounds from now · bottom: your index then</p>
      <p style={{ fontFamily: SANS, fontSize: 12, lineHeight: 1.5, color: MID, marginTop: 10 }}>
        It can climb freely to {n.soft_cap_at} (your 12-month low of {n.low_12m}, plus 3). Above that, WHS halves every rise, and it can never pass {n.hard_cap_at}.
      </p>
    </>
  );
}

// Defined at module level so React keeps the same element type across renders
// (an inline component would remount and drop focus on every keystroke).
function FieldInput({ k, label, w = 1, f, set }) {
  return (
    <div style={{ flex: w }}>
      <div style={{ fontFamily: MONO, fontSize: 8, color: DIM, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 3 }}>{label}</div>
      <input value={f[k] ?? ''} onChange={e => set(k, e.target.value)} style={input} />
    </div>
  );
}

// ── Confirm card: one proposal, editable, with played-with chips ─────────────
function ProposalCard({ p, players, onSaved, onDiscard }) {
  const [f, setF] = useState({ ...p, played_with: [] });
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));
  const num = (v) => (v === '' || v == null ? null : Number(v));
  const complete = f.course_rating && f.slope && f.tee;
  const toggle = (n) => set('played_with', f.played_with.includes(n) ? f.played_with.filter(x => x !== n) : [...f.played_with, n]);
  const addName = () => { const n = name.trim(); if (n && !f.played_with.includes(n)) set('played_with', [...f.played_with, n]); setName(''); };

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      const body = {
        date: f.date, course: f.course, tee: f.tee || null, par: num(f.par), course_rating: num(f.course_rating), slope: num(f.slope),
        pcc: num(f.pcc) || 0, course_hcp: num(f.course_hcp), adj_gross: num(f.adj_gross), score_diff: num(f.score_diff),
        index_before: num(f.index_before), index_after: num(f.index_after), stableford: num(f.stableford),
        holes: f.holes || null, played_with: f.played_with, source: f.source || 'screenshot',
      };
      await api.post('/verve/golf/rounds', body);
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };


  return (
    <div style={{ background: 'rgba(255,255,255,0.03)', border: `0.5px solid ${complete ? GREEN : AMBER}55`, borderRadius: 8, padding: 14, marginBottom: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <span style={{ width: 7, height: 7, borderRadius: '50%', background: complete ? GREEN : AMBER }} />
        <span style={{ fontFamily: MONO, fontSize: 10, color: complete ? GREEN : AMBER }}>
          {complete ? (p.validation?.diff_matches === false ? 'rated, but diff does not reproduce' : 'rated') : 'unrated: add tee, rating and slope (Score Detail screen)'}
        </span>
        {p.rating_source === 'course_table' && <span style={{ fontFamily: MONO, fontSize: 9, color: DIM }}>· rating from course table</span>}
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}><FieldInput f={f} set={set} k="date" label="Date" w={1} /><FieldInput f={f} set={set} k="course" label="Course" w={2} /></div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}><FieldInput f={f} set={set} k="tee" label="Tee" /><FieldInput f={f} set={set} k="adj_gross" label="Gross" /><FieldInput f={f} set={set} k="score_diff" label="Diff" /><FieldInput f={f} set={set} k="index_before" label="Index off" /></div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}><FieldInput f={f} set={set} k="course_rating" label="CR" /><FieldInput f={f} set={set} k="slope" label="Slope" /><FieldInput f={f} set={set} k="pcc" label="PCC" /><FieldInput f={f} set={set} k="par" label="Par" /><FieldInput f={f} set={set} k="index_after" label="New idx" /></div>
      <div style={{ fontFamily: MONO, fontSize: 8, color: DIM, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 6 }}>Played with</div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
        {[...new Set([...players.map(x => x.name), ...f.played_with])].map(n => (
          <button key={n} onClick={() => toggle(n)} style={{ ...btn(f.played_with.includes(n)), borderRadius: 14, padding: '4px 10px' }}>{n}</button>
        ))}
        <input value={name} onChange={e => setName(e.target.value)} onKeyDown={e => e.key === 'Enter' && addName()} placeholder="+ name" style={{ ...input, width: 90, padding: '4px 8px' }} />
      </div>
      {err && <p style={{ fontFamily: MONO, fontSize: 10, color: RED, marginBottom: 6 }}>{err}</p>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={save} disabled={busy} style={{ ...btn(true), flex: 1, padding: 9 }}>{busy ? 'Saving…' : 'Save round'}</button>
        <button onClick={onDiscard} style={{ ...btn(false), padding: '9px 12px' }}>Discard</button>
      </div>
    </div>
  );
}

// ── Add overlay: photo / screenshot / paste ──────────────────────────────────
function AddOverlay({ onClose, onSaved, players }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [paste, setPaste] = useState('');
  const [err, setErr] = useState(null);
  const fileRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true); setErr(null); setResult(null);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await api.post('/verve/golf/extract', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(r.data);
    } catch (e) { setErr(e?.response?.data?.detail || 'Could not read the image'); }
    finally { setBusy(false); }
  };
  const submitPaste = async () => {
    setBusy(true); setErr(null); setResult(null);
    try { const r = await api.post('/verve/golf/paste', { text: paste }); setResult(r.data); }
    catch (e) { setErr(e?.response?.data?.detail || 'Nothing parsed'); }
    finally { setBusy(false); }
  };
  const drop = (i) => setResult(r => ({ ...r, proposals: r.proposals.filter((_, j) => j !== i) }));

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#0a0a0f', zIndex: 100, display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '16px 20px', borderBottom: '0.5px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ fontFamily: MONO, fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: MID, flex: 1 }}>Add round</span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: DIM, fontFamily: MONO, fontSize: 12, cursor: 'pointer' }}>Close</button>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 20px 40px' }} onPaste={(e) => {
        const it = Array.from(e.clipboardData?.items || []).find(i => i.kind === 'file');
        if (it) { e.preventDefault(); upload(it.getAsFile()); }
      }}>
        <Label>Photo or screenshot</Label>
        <p style={{ fontFamily: SANS, fontSize: 12, color: DIM, marginBottom: 10 }}>HNA Score Detail screen (best), the Scores list, or a paper scorecard. Paste works too.</p>
        <input ref={fileRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={e => upload(e.target.files?.[0])} />
        <button onClick={() => fileRef.current?.click()} style={{ ...btn(true), width: '100%', padding: 12, fontSize: 11 }}>Choose image</button>

        <Label>Or paste the HNA My Scores page</Label>
        <textarea value={paste} onChange={e => setPaste(e.target.value)} rows={3} placeholder="Copy the table from handicaps.co.za and paste here" style={{ ...input, fontFamily: SANS, resize: 'vertical' }} />
        <button onClick={submitPaste} disabled={!paste.trim() || busy} style={{ ...btn(false), marginTop: 8 }}>Parse paste</button>

        {busy && <div style={{ marginTop: 20 }}><ZLoadingBar accent={RED} loading={true} /></div>}
        {err && <p style={{ fontFamily: MONO, fontSize: 11, color: RED, marginTop: 14 }}>{err}</p>}

        {result && (
          <>
            <Label>{result.proposals.length ? `${result.proposals.length} to confirm` : 'Nothing new'}</Label>
            {result.duplicates?.length > 0 && (
              <p style={{ fontFamily: MONO, fontSize: 10, color: DIM, marginBottom: 10 }}>
                Already stored: {result.duplicates.map(d => `${fmtDate(d.date)} ${short(d.course)} ${d.adj_gross}`).join(' · ')}
              </p>
            )}
            {result.proposals.map((p, i) => (
              <ProposalCard key={`${p.date}-${p.course}-${i}`} p={p} players={players}
                onSaved={() => { drop(i); onSaved(); }} onDiscard={() => drop(i)} />
            ))}
            {result.notes && <p style={{ fontFamily: MONO, fontSize: 9, color: DIM }}>{result.notes}</p>}
          </>
        )}
      </div>
    </div>
  );
}

// ── Course outlook ───────────────────────────────────────────────────────────
function CourseLookup({ courses, onOpen }) {
  const [q, setQ] = useState('');
  const [o, setO] = useState(null);
  useEffect(() => {
    if (!q.trim()) { setO(null); return; }
    const t = setTimeout(() => api.get(`/verve/golf/course?name=${encodeURIComponent(q)}`).then(r => setO(r.data)).catch(() => setO(null)), 300);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <>
      <input list="golf-courses" value={q} onChange={e => setQ(e.target.value)} placeholder="Where are you playing?" style={input} />
      <datalist id="golf-courses">{courses.map(c => <option key={c.course} value={c.course} />)}</datalist>
      {o && o.rounds > 0 && (
        <div style={{ marginTop: 10 }}>
          <Row k="Rounds here" v={o.rounds} sub={`last ${fmtDate(o.last_played)}, shot ${o.last_gross}`} />
          <Row k="Average / best" v={`${o.avg_gross} / ${o.best_gross}`} />
          {o.rating?.course_rating && <Row k="Rating" v={`${o.tee || ''} ${o.rating.course_rating} / ${o.rating.slope}`} sub={o.course_hcp != null ? `course hcp ${o.course_hcp}` : ''} />}
          {o.targets && <Row k="To play to index" v={o.targets.to_play_to_index} color={GREEN} />}
          {o.targets?.to_lower_index != null && <Row k="To lower index" v={o.targets.to_lower_index} sub={`index to ${o.targets.lowered_to}`} color={GREEN} />}
          <button onClick={() => onOpen(o.course)} style={{ ...btn(false), marginTop: 8 }}>Every round here</button>
        </div>
      )}
    </>
  );
}

// ── Drill-down sheet: a course or a year ────────────────────────────────────
function RoundLine({ r, onCourse }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, padding: '7px 0', borderBottom: '0.5px solid rgba(255,255,255,0.04)' }}>
      <span style={{ fontFamily: MONO, fontSize: 10, color: DIM, width: 58, flexShrink: 0 }}>{fmtDate(r.date)}</span>
      <span onClick={onCourse ? () => onCourse(r.course) : undefined} style={{ fontFamily: SANS, fontSize: 12, color: TEXT, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: onCourse ? 'pointer' : 'default' }}>
        {short(r.course)}{r.tee ? <span style={{ color: DIM, fontSize: 10 }}> {r.tee.split(' ')[0]}</span> : null}
      </span>
      <span style={{ fontFamily: MONO, fontSize: 13, color: r.played_to_index ? GREEN : TEXT }}>{r.adj_gross}</span>
      <span style={{ fontFamily: MONO, fontSize: 10, color: DIM, width: 34, textAlign: 'right' }}>{r.score_diff}</span>
      <span style={{ fontFamily: MONO, fontSize: 10, color: DIM, width: 26, textAlign: 'right' }}>{r.index_before}</span>
    </div>
  );
}

function CourseDetail({ name, onCourse }) {
  const [o, setO] = useState(null);
  const [rounds, setRounds] = useState(null);
  useEffect(() => {
    setO(null); setRounds(null);
    api.get(`/verve/golf/course?name=${encodeURIComponent(name)}`).then(r => setO(r.data)).catch(() => {});
    api.get(`/verve/golf/rounds?course=${encodeURIComponent(name)}&limit=200`).then(r => setRounds(r.data.rounds)).catch(() => {});
  }, [name]);
  if (!o || !rounds) return <ZLoadingBar accent={RED} loading={true} />;
  const years = {};
  rounds.forEach(r => { const y = r.date.slice(0, 4); years[y] = years[y] || { n: 0, sum: 0 }; years[y].n++; years[y].sum += r.adj_gross; });
  const best = rounds.reduce((a, r) => (a == null || r.adj_gross < a.adj_gross ? r : a), null);
  return (
    <>
      <Row k="Rounds" v={o.rounds} sub={`${Object.keys(years).length} seasons`} />
      <Row k="Average / best" v={`${o.avg_gross} / ${o.best_gross}`} sub={best ? fmtDate(best.date) : ''} />
      <Row k="Avg differential" v={o.avg_diff} />
      {o.rating?.course_rating && <Row k="Rating" v={`${o.tee || ''} ${o.rating.course_rating} / ${o.rating.slope}`} sub={o.course_hcp != null ? `course hcp ${o.course_hcp}` : ''} />}
      {o.targets && <Row k="To play to index" v={o.targets.to_play_to_index} color={GREEN} />}
      {o.targets?.to_lower_index != null && <Row k="To lower index" v={o.targets.to_lower_index} color={GREEN} />}
      <Label>By year</Label>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {Object.entries(years).sort().map(([y, v]) => (
          <span key={y} style={{ fontFamily: MONO, fontSize: 10, color: TEXT, background: 'rgba(255,255,255,0.04)', borderRadius: 12, padding: '4px 10px' }}>
            {y} <span style={{ color: DIM }}>{v.n}× · {(v.sum / v.n).toFixed(1)}</span>
          </span>
        ))}
      </div>
      <Label>Every round</Label>
      <div style={{ display: 'flex', gap: 8, fontFamily: MONO, fontSize: 8, color: DIM, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 2 }}>
        <span style={{ width: 58 }}>date</span><span style={{ flex: 1 }}>course</span><span>gross</span><span style={{ width: 34, textAlign: 'right' }}>diff</span><span style={{ width: 26, textAlign: 'right' }}>idx</span>
      </div>
      {rounds.map(r => <RoundLine key={r.id || r.key} r={r} />)}
    </>
  );
}

function YearDetail({ year, onCourse }) {
  const [rounds, setRounds] = useState(null);
  useEffect(() => { setRounds(null); api.get(`/verve/golf/rounds?year=${year}&limit=200`).then(r => setRounds(r.data.rounds)).catch(() => {}); }, [year]);
  if (!rounds) return <ZLoadingBar accent={RED} loading={true} />;
  const by = {};
  rounds.forEach(r => { const b = by[r.course] = by[r.course] || { course: r.course, n: 0, sum: 0, dsum: 0, best: 99 }; b.n++; b.sum += r.adj_gross; b.dsum += r.score_diff ?? 0; b.best = Math.min(b.best, r.adj_gross); });
  const courses = Object.values(by).sort((a, b) => b.n - a.n);
  const avg = rounds.reduce((a, r) => a + r.adj_gross, 0) / rounds.length;
  return (
    <>
      <Row k="Rounds" v={rounds.length} sub={`${courses.length} courses`} />
      <Row k="Average gross" v={avg.toFixed(1)} />
      <Row k="Played to index" v={`${Math.round(100 * rounds.filter(r => r.played_to_index).length / rounds.length)}%`} />
      <Label>Courses</Label>
      {courses.map(c => (
        <div key={c.course} onClick={() => onCourse(c.course)} style={{ cursor: 'pointer' }}>
          <Row k={short(c.course)} v={(c.sum / c.n).toFixed(1)} sub={`${c.n}× · best ${c.best} · diff ${(c.dsum / c.n).toFixed(1)}`} />
        </div>
      ))}
      <Label>Every round</Label>
      <div style={{ display: 'flex', gap: 8, fontFamily: MONO, fontSize: 8, color: DIM, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 2 }}>
        <span style={{ width: 58 }}>date</span><span style={{ flex: 1 }}>course</span><span>gross</span><span style={{ width: 34, textAlign: 'right' }}>diff</span><span style={{ width: 26, textAlign: 'right' }}>idx</span>
      </div>
      {rounds.map(r => <RoundLine key={r.id || r.key} r={r} onCourse={onCourse} />)}
    </>
  );
}

function DetailSheet({ sel, onClose, onCourse }) {
  if (!sel) return null;
  const title = sel.kind === 'course' ? short(sel.value) : String(sel.value);
  return (
    <div style={{ position: 'fixed', inset: 0, background: '#0a0a0f', zIndex: 90, display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '16px 20px', borderBottom: '0.5px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ fontFamily: SERIF, fontSize: 22, color: TEXT, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: DIM, fontFamily: MONO, fontSize: 12, cursor: 'pointer' }}>Close</button>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 20px 40px' }}>
        {sel.kind === 'course' ? <CourseDetail name={sel.value} onCourse={onCourse} /> : <YearDetail year={sel.value} onCourse={onCourse} />}
      </div>
    </div>
  );
}

// ── Tab ──────────────────────────────────────────────────────────────────────
const NO_TARGETS = 'Targets appear once there are 20 rounds in the window.';
const signed = (v) => (v == null ? '-' : `${v > 0 ? '+' : ''}${v}`);

export default function GolfTab() {
  const [d, setD] = useState(null);
  const [players, setPlayers] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [pick, setPick] = useState(null);
  const [sel, setSel] = useState(null);
  const [openTile, setOpenTile] = useState(null);
  const openCourse = (name) => setSel({ kind: 'course', value: name });

  const load = async () => {
    const [dash, pl] = await Promise.allSettled([api.get('/verve/golf/dashboard'), api.get('/verve/golf/players')]);
    if (dash.status === 'fulfilled') setD(dash.value.data);
    if (pl.status === 'fulfilled') setPlayers(pl.value.data.players || []);
  };
  useEffect(() => { load(); }, []);

  const cur = d?.current;
  const story = useMemo(() => (cur ? buildStory(cur, d.series) : []), [cur, d]);

  if (!d) return <div style={{ padding: 24 }}><ZLoadingBar accent={RED} loading={true} /></div>;
  if (!d.rounds) return <ZEmptyState mark="G" accent={RED} message="No rounds yet" hint="Seed from HNA, then add rounds from the app" />;

  const n = cur.next;
  const idxLow = Math.min(...d.years.map(y => y.index_low).filter(v => v != null));
  const idxHigh = Math.max(...d.years.map(y => y.index_high).filter(v => v != null));
  const defaultCourse = d.series[d.series.length - 1]?.course;

  // Collapsed summaries: each is a fact already on the page, picked out.
  const bestYear = d.years.filter(y => y.avg_over_cr != null).reduce((a, y) => (a == null || y.avg_over_cr < a.avg_over_cr ? y : a), null);
  const best = d.records.best[0];
  const bestCourse = d.courses.filter(c => c.rounds >= 3 && c.avg_diff != null).reduce((a, c) => (a == null || c.avg_diff < a.avg_diff ? c : a), null);
  const nextC = defaultRated(ratedCourses(d.courses), defaultCourse);
  const path = n?.drift_at_average?.path || [];
  const normal = typicalGap(d.series);

  const tiles = [
    { key: 'years', title: 'Years',
      summary: `${d.years.length} seasons${bestYear ? ` · best ${bestYear.year}, ${signed(bestYear.avg_over_cr)} over rating` : ''}`,
      body: (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: MONO, fontSize: 10 }}>
            <thead><tr style={{ color: DIM }}>{['yr', 'n', 'gross', 'diff', '+cr', 'sd', '<80', 'best', 'worst'].map(h => <th key={h} style={{ textAlign: 'right', padding: '4px 4px', fontWeight: 400 }}>{h}</th>)}</tr></thead>
            <tbody>{d.years.map(y => (
              <tr key={y.year} onClick={() => setSel({ kind: 'year', value: y.year })} style={{ color: TEXT, borderTop: '0.5px solid rgba(255,255,255,0.04)', cursor: 'pointer' }}>
                <td style={{ padding: '5px 4px', textAlign: 'right' }}>{y.year}</td><td style={{ textAlign: 'right' }}>{y.rounds}</td>
                <td style={{ textAlign: 'right' }}>{y.avg_gross}</td><td style={{ textAlign: 'right' }}>{y.avg_diff}</td>
                <td style={{ textAlign: 'right' }}>{y.avg_over_cr}</td><td style={{ textAlign: 'right' }}>{y.sd_diff}</td>
                <td style={{ textAlign: 'right' }}>{y.pct_under_80}%</td>
                <td style={{ textAlign: 'right', color: GREEN }}>{y.best.adj_gross}</td><td style={{ textAlign: 'right', color: RED }}>{y.worst.adj_gross}</td>
              </tr>))}</tbody>
          </table>
          <p style={{ fontFamily: MONO, fontSize: 9, color: DIM, marginTop: 6 }}>gross: average score · diff: average differential · +cr: strokes over course rating, the fair year-on-year measure since courses were re-rated around 2022 · sd: how erratic · &lt;80: share of rounds under 80</p>
        </div>
      ) },
    { key: 'records', title: 'Records',
      summary: best ? `best ${best.adj_gross} at ${short(best.course)}, ${fmtDate(best.date)} · ${d.records.under_rating} under rating` : 'No records yet',
      body: (
        <>
          {d.records.best.slice(0, 6).map(r => <div key={`b${r.date}${r.course}`} onClick={() => openCourse(r.course)} style={{ cursor: 'pointer' }}><Row k={`${fmtDate(r.date)} ${short(r.course)}`} v={r.adj_gross} sub={`diff ${r.score_diff}`} color={GREEN} /></div>)}
          <div style={{ height: 10 }} />
          {d.records.worst.slice(0, 4).map(r => <div key={`w${r.date}${r.course}`} onClick={() => openCourse(r.course)} style={{ cursor: 'pointer' }}><Row k={`${fmtDate(r.date)} ${short(r.course)}`} v={r.adj_gross} sub={`diff ${r.score_diff}`} color={RED} /></div>)}
          <p style={{ fontFamily: MONO, fontSize: 9, color: DIM, marginTop: 8 }}>{d.records.under_rating} rounds better than the course rating.</p>
        </>
      ) },
    { key: 'courses', title: 'Courses',
      summary: `${d.courses.length} courses${bestCourse ? ` · best ${short(bestCourse.course)} ${bestCourse.avg_diff}` : ''}`,
      body: (
        <>
          <CourseLookup courses={d.courses} onOpen={openCourse} />
          <div style={{ height: 12 }} />
          {d.courses.filter(c => c.rounds >= 3).slice(0, 25).map(c => (
            <div key={c.course} onClick={() => openCourse(c.course)} style={{ cursor: 'pointer' }}>
              <Row k={short(c.course)} v={c.avg_diff} sub={`${c.rounds} rounds · best ${c.best_gross}`} color={c.avg_diff <= cur.career_avg_diff - 1 ? GREEN : c.avg_diff >= cur.career_avg_diff + 1 ? RED : TEXT} />
            </div>
          ))}
        </>
      ) },
    { key: 'index', title: 'Index history',
      summary: `${d.first_round.slice(0, 4)} to now · low ${idxLow} · high ${idxHigh}`,
      body: (
        <>
          <IndexChart series={d.series} onPick={(p) => setPick({ ...p, src: 'chart' })} />
          <p style={{ fontFamily: MONO, fontSize: 9, color: DIM, marginTop: 4 }}>red: your official index · grey: what it would be with no cap · low {idxLow} · high {idxHigh}</p>
          {pick?.src === 'chart' && <PickBox pick={pick} onCourse={openCourse} />}
        </>
      ) },
    { key: 'next', title: 'Next round',
      summary: n ? (nextC ? nextRoundLine(n, nextC.latest_rating) : 'No rated course yet.') : NO_TARGETS,
      body: n ? <NextRound n={n} courses={d.courses} defaultCourse={defaultCourse} /> : <p style={{ fontFamily: SANS, fontSize: 12, color: DIM }}>{NO_TARGETS}</p> },
    { key: 'why', title: `Why it's ${f1(cur.index)}`,
      summary: [n?.counting_avg != null ? `best 8 of 20 average ${n.counting_avg}` : null, cur.eighth_best_diff != null ? `8th best ${cur.eighth_best_diff}` : null].filter(Boolean).join(' · '),
      body: (
        <>
          <WindowBars window={cur.window} eighth={cur.eighth_best_diff} lowerAt={n?.lower_max_diff} onPick={(w) => setPick({ ...w, src: 'bars' })} />
          <p style={{ fontFamily: SANS, fontSize: 12, lineHeight: 1.5, color: MID, marginTop: 6 }}>
            Each bar is one of your last 20 rounds, oldest on the left. Taller is worse. Your index is the average of the 8 green ones{n?.counting_avg != null ? ` (${n.counting_avg})` : ''}.
            The number under a green bar is how many rounds until it drops out; the red-outlined bar leaves next.
            {cur.eighth_best_diff != null ? ` Amber line: your 8th best (${cur.eighth_best_diff}).` : ''}
            {n?.lower_max_diff != null ? ` Green line: a round under it lowers the index.` : ''}
          </p>
          {pick?.src === 'bars' && <PickBox pick={pick} onCourse={openCourse} />}
        </>
      ) },
    { key: 'coming', title: "What's coming",
      summary: path.length ? `play your average: ${f1(path[path.length - 1])} in ${path.length} rounds` : NO_TARGETS,
      body: n ? <Drift n={n} /> : <p style={{ fontFamily: SANS, fontSize: 12, color: DIM }}>{NO_TARGETS}</p> },
    { key: 'form', title: 'Form',
      summary: cur.gap_to_index != null ? `gap ${f1(cur.gap_to_index)} to index${normal != null ? `, usual ${f1(normal)}` : ''}` : '',
      body: <Story lines={story} /> },
  ];

  return (
    <div style={{ padding: '0 20px 140px', overflowY: 'auto', overflowX: 'hidden', flex: 1 }}>
      {/* Hero: the number, the cap note, the first line of the story */}
      <div style={{ position: 'relative', padding: '28px 0 18px', borderBottom: '0.5px solid rgba(255,255,255,0.06)' }}>
        <button onClick={() => setShowAdd(true)} style={{ ...btn(true), position: 'absolute', top: 16, right: 0 }}>+ Add round</button>
        <div style={{ textAlign: 'center' }}>
          <p style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '0.2em', color: DIM, marginBottom: 8, textTransform: 'uppercase' }}>Handicap index</p>
          <p style={{ fontFamily: SERIF, fontSize: 56, fontWeight: 300, color: TEXT, lineHeight: 1 }}>{f1(cur.index)}</p>
          {cur.uncapped != null && cur.uncapped !== cur.index && <p style={{ fontFamily: MONO, fontSize: 10, color: AMBER, marginTop: 4 }}>uncapped {cur.uncapped}: soft cap in force</p>}
        </div>
        <Story lines={story.slice(0, 1)} />
      </div>

      {d.unrated?.length > 0 && (
        <p style={{ fontFamily: MONO, fontSize: 10, color: AMBER, marginTop: 14 }}>{d.unrated.length} round{d.unrated.length > 1 ? 's' : ''} missing tee/rating: open Score Detail in HNA and add it.</p>
      )}

      <div style={{ marginTop: 8 }}>
        {tiles.map(t => (
          <ZTile key={t.key} title={t.title} summary={t.summary} accent={RED}
            open={openTile === t.key} onToggle={() => setOpenTile(openTile === t.key ? null : t.key)}>
            {t.body}
          </ZTile>
        ))}
      </div>

      <DetailSheet sel={sel} onClose={() => setSel(null)} onCourse={openCourse} />
      {showAdd && <AddOverlay players={players} onClose={() => setShowAdd(false)} onSaved={load} />}
    </div>
  );
}
